import React, { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-xl px-3 py-2 shadow-warm-md border border-surface-200 text-sm space-y-1">
      <p className="font-semibold text-ink-800">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="text-ink-500 flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span>{p.name}:</span>
          <span className="font-medium">{(p.value || 0).toLocaleString()}</span>
        </p>
      ))}
    </div>
  );
};

function unescapePathKey(key) {
  return key.replace(/_/g, '/');
}

const LandingBlogChart = ({ pageviews }) => {
  const data = (pageviews || []).map((pv) => ({
    date: pv.id?.slice(5),
    landing: pv.landing || 0,
    blog: pv.blog || 0,
  }));

  // Aggregate byPath across the range to surface blog leaderboard.
  const leaderboard = useMemo(() => {
    const byPath = {};
    for (const pv of pageviews || []) {
      const paths = pv.byPath || {};
      for (const [k, v] of Object.entries(paths)) {
        const p = unescapePathKey(k);
        if (p.startsWith('/blog/')) {
          byPath[p] = (byPath[p] || 0) + (v || 0);
        }
      }
    }
    return Object.entries(byPath)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
  }, [pageviews]);

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-bold text-ink-900 text-[0.9375rem]">Marketing traffic</h3>
        <span className="text-xs text-ink-400">Last 30 days · landing + blog</span>
      </div>
      <p className="text-xs text-ink-400 mb-5">
        Public pageviews, session-deduped client-side. Light noise expected from scrapers.
      </p>
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="lb-canopy" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#1B6B4A" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#1B6B4A" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="lb-blush" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#F2886D" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#F2886D" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e0d6" vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#57534e' }} axisLine={{ stroke: '#e5e0d6' }} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: '#57534e' }} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip content={<CustomTooltip />} />
          <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
          <Area type="monotone" dataKey="landing" name="Landing" stackId="1" stroke="#1B6B4A" strokeWidth={2} fill="url(#lb-canopy)" />
          <Area type="monotone" dataKey="blog" name="Blog" stackId="1" stroke="#F2886D" strokeWidth={2} fill="url(#lb-blush)" />
        </AreaChart>
      </ResponsiveContainer>

      {leaderboard.length > 0 && (
        <div className="mt-6 pt-5 border-t border-surface-100">
          <h4 className="font-semibold text-ink-900 text-sm mb-3">Top blog posts</h4>
          <div className="space-y-1.5">
            {leaderboard.map(([path, count], idx) => (
              <div key={path} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-3 min-w-0">
                  <span className="text-xs font-semibold text-ink-400 w-5">{idx + 1}</span>
                  <span className="text-ink-700 truncate font-medium">{path.replace('/blog/', '')}</span>
                </span>
                <span className="text-ink-500 font-semibold tabular-nums">{count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingBlogChart;
